import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ChevronRight, ChevronLeft, X, Sparkles } from "lucide-react";
import { Button } from "./ui/button";

const TOUR_STEPS = [
  {
    target: '[data-testid="nav-dashboard"]',
    route: "/dashboard",
    title: "Dashboard",
    description: "Your command centre — contract metrics, status breakdown, and recent activity at a glance.",
    position: "right",
  },
  {
    target: '[data-testid="nav-contracts"]',
    route: "/contracts",
    title: "Contracts",
    description: "All contracts live here. Search, filter, and click any row for full details.",
    position: "right",
  },
  {
    target: '[data-testid="create-contract-btn"]',
    route: "/contracts",
    title: "Create a Contract",
    description: "Start a new contract with the 5-step wizard, or clone from an existing one.",
    position: "bottom",
  },
  {
    target: '[data-testid="nav-clause-library"]',
    route: "/clauses",
    title: "Clause Library",
    description: "Reusable legal clauses for contract drafting. Benchmark any clause against industry best practices with AI.",
    position: "right",
  },
  {
    target: '[data-testid="nav-approval-policies"]',
    route: "/approval-policies",
    title: "Approval Policies",
    description: "Define attribute-based rules (value, type, category) that auto-assign specific approvers. Contracts are blocked without a matching policy.",
    position: "right",
  },
  {
    target: '[data-testid="nav-workflows"]',
    route: "/workflows",
    title: "Approval Workflows",
    description: "Track contracts through review and approval. Reviewers forward or reject — only assigned approvers can approve.",
    position: "right",
  },
  {
    target: '[data-testid="nav-management"]',
    route: "/contract-management",
    title: "Contract Management",
    description: "Post-signature lifecycle — deliverables, KPIs, milestones, obligations, and management reports. Renewal alerts notify you automatically.",
    position: "right",
  },
  {
    target: '[data-testid="nav-reports"]',
    route: "/reports",
    title: "Reports & Analytics",
    description: "Generate contract register, expiry tracker, compliance, and approval cycle reports. Export as CSV, PDF, or HTML.",
    position: "right",
  },
  {
    target: '[data-testid="nav-user-management"]',
    route: "/users",
    title: "User Management",
    description: "Invite users via email, assign roles, manage seat limits, and control permissions. Only visible to admins.",
    position: "right",
    adminOnly: true,
  },
  {
    target: '[data-testid="nav-help-&-guide"]',
    route: "/help",
    title: "Help & Guide",
    description: "Full role-based documentation with step-by-step instructions. You can restart this tour anytime from here.",
    position: "right",
  },
];

export default function OnboardingTour({ userRole, onComplete }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState(0);
  const [spotlightRect, setSpotlightRect] = useState(null);
  const [tooltipStyle, setTooltipStyle] = useState({});
  const [transitioning, setTransitioning] = useState(false);

  const isAdmin = ["superuser", "admin"].includes(userRole);
  const steps = TOUR_STEPS.filter((s) => !s.adminOnly || isAdmin);
  const currentStep = steps[step];

  const positionTooltip = useCallback(() => {
    if (!currentStep) return;
    const el = document.querySelector(currentStep.target);
    if (!el) {
      setSpotlightRect(null);
      setTooltipStyle({ top: "50%", left: "50%", transform: "translate(-50%, -50%)" });
      return;
    }

    el.scrollIntoView({ block: "nearest", inline: "nearest", behavior: "smooth" });

    const rect = el.getBoundingClientRect();
    const pad = 6;
    setSpotlightRect({
      top: rect.top - pad,
      left: rect.left - pad,
      width: rect.width + pad * 2,
      height: rect.height + pad * 2,
    });

    const pos = currentStep.position || "right";
    const tt = {};
    if (pos === "right") {
      tt.top = rect.top;
      tt.left = rect.right + 16;
    } else if (pos === "bottom") {
      tt.top = rect.bottom + 12;
      tt.left = rect.left;
    } else if (pos === "left") {
      tt.top = rect.top;
      tt.right = window.innerWidth - rect.left + 16;
    }

    if (tt.left !== undefined && tt.left + 320 > window.innerWidth) {
      tt.left = window.innerWidth - 340;
    }
    if (tt.top !== undefined && tt.top + 200 > window.innerHeight) {
      tt.top = window.innerHeight - 220;
    }

    setTooltipStyle(tt);
  }, [currentStep]);

  const navigateAndPosition = useCallback(() => {
    if (!currentStep) return;
    if (currentStep.route && location.pathname !== currentStep.route) {
      setTransitioning(true);
      navigate(currentStep.route);
      setTimeout(() => {
        positionTooltip();
        setTransitioning(false);
      }, 600);
    } else {
      positionTooltip();
    }
  }, [currentStep, location.pathname, navigate, positionTooltip]);

  // Position on mount and step change
  useEffect(() => {
    navigateAndPosition();
  }, [step, navigateAndPosition]);

  // Lock body scroll during tour
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);
  // Reposition on window resize
  useEffect(() => {
    const handleResize = () => positionTooltip();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [positionTooltip]);

  const next = () => {
    if (step < steps.length - 1) {
      setStep(step + 1);
    } else {
      finish();
    }
  };

  const prev = () => {
    if (step > 0) setStep(step - 1);
  };

  const finish = () => {
    if (onComplete) onComplete();
  };

  return (
    <div className="fixed inset-0 z-[9999]" data-testid="onboarding-tour">
      {/* Overlay — spotlight uses box-shadow to darken everything except the target */}
      <div className="absolute inset-0 pointer-events-none">
        {spotlightRect ? (
          <div
            className="absolute rounded-md transition-all duration-300 ease-out"
            style={{
              top: spotlightRect.top,
              left: spotlightRect.left,
              width: spotlightRect.width,
              height: spotlightRect.height,
              boxShadow: "0 0 0 9999px rgba(0,0,0,0.55)",
              background: "transparent",
              border: "2px solid rgba(34,211,238,0.6)",
            }}
            data-testid="tour-spotlight"
          />
        ) : (
          <div className="absolute inset-0" style={{ background: "rgba(0,0,0,0.55)" }} />
        )}
      </div>

      {/* Tooltip card */}
      {!transitioning && currentStep && (
        <div
          className="absolute z-[10000] bg-white dark:bg-slate-900 rounded-lg shadow-2xl border border-slate-200 dark:border-slate-700 w-[320px] animate-in fade-in duration-200"
          style={tooltipStyle}
          data-testid="tour-tooltip"
        >
          <div className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Sparkles size={14} className="text-cyan-500" />
                <span className="text-[11px] font-medium text-cyan-600 uppercase tracking-wide">
                  Step {step + 1} of {steps.length}
                </span>
              </div>
              <button onClick={finish} className="text-slate-400 hover:text-slate-600 transition-colors" data-testid="tour-close">
                <X size={14} />
              </button>
            </div>

            <div className="flex gap-1 mb-4">
              {steps.map((_, i) => (
                <div
                  key={i}
                  className={`h-1 flex-1 rounded-full transition-colors ${i <= step ? "bg-cyan-500" : "bg-slate-200 dark:bg-slate-700"}`}
                />
              ))}
            </div>

            <h3 className="text-base font-semibold text-slate-900 dark:text-white mb-1.5">
              {currentStep.title}
            </h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">
              {currentStep.description}
            </p>
          </div>

          <div className="flex items-center justify-between px-5 py-3 border-t border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50 rounded-b-lg">
            <button
              onClick={finish}
              className="text-xs text-slate-400 hover:text-slate-600 transition-colors"
              data-testid="tour-skip"
            >
              Skip tour
            </button>
            <div className="flex items-center gap-2">
              {step > 0 && (
                <Button variant="ghost" size="sm" onClick={prev} className="h-8 px-3 text-xs gap-1" data-testid="tour-prev">
                  <ChevronLeft size={14} /> Back
                </Button>
              )}
              <Button size="sm" onClick={next} className="h-8 px-4 text-xs gap-1 bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="tour-next">
                {step === steps.length - 1 ? "Finish" : "Next"} <ChevronRight size={14} />
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
